// Platform Guide (in-app usage; account registration/login/settings handled separately)
const help = {

  start: {
    title: "Help & Support",
    subtitle: "Guides, FAQs, and support contact. Pick a section below.",

    ui: {
      back: "Back",
      open: "Open",
    },

    sections: {
      account: {
        title: "Account Guide",
        subtitle: "Guide for registration, login & settings.",
      },
      platform: {
        title: "Platform Guide",
        subtitle: "How Elnydo works",
      },
      ads: {
        title: "Advertisement Guide",
        subtitle: "Creating ads, targeting, budgets, review rules, and billing.",
      },
      faq: {
        title: "FAQ",
        subtitle: "Quick answers — grouped by topic so users can find things fast.",
      },
    },

    contact: {
      title: "Contact Support",
      subtitle: "If you need additional help, click here to contact us.",
      cta: "Contact",
    },
  },

  
  account: {
    pageTitle: "Account Administration",
    pageSubtitle:
      "Create and manage your account, recover access, and control your settings for privacy, security, and notifications.",

    ui: {
      back: "Back",
      backAria: "Back",
      backTitle: "Back",
      jumpHint: "Jump to a topic:",
      overview: "Overview",
      copyLink: "Copy link",
      backToTop: "Back to top",
      linkToTopicAria: "Link to {{title}}",
      linkToTopicTitle: "Link to {{title}}",
    },

    topics: {
      intro: {
        title: "Account overview",
        bullets: [
          "Your Elnydo account is the identity and platform used for your profile, posts, likes, follows, and notifications (all the user specific content) across the platform.",
          "Essential account data includes profile name (max 18 letters), your Email and password secured by hash (encrypted).",
          "Account data (Email, password) can be changed in the settings section. It is not possible to change the profile name of a existing account.",
        ],
      },

      registration: {
        title: "Account registration",
        bullets: [
          "To create a profile, you must provide: a profile name, a valid Email, and a password.",
          "Your profile name is not changeable after creation.",
          "You must confirm that you are 18 years or older (required) to create an account.",
          "All fields (Profile name, valid Email, password (encrypted) & min. age of 18 confirmation) are required to create your profile successfully.",
        ],
      },

      login: {
        title: "Log in",
        bullets: [
          "To log in, enter your Email and password.",
          "If you are logged out unexpectedly, check whether your session was ended (for example after a security update or logging out on another device).",
        ],
      },

      forgotPassword: {
        title: "Forgot password",
        bullets: [
          "If you forget your password, use “Forgot password” function on the login screen.",
          "A password reset link will be sent to your account's Email.",
          "Open the link we sended, and choose a new password to regain access to your account.",
          "After confirming the new password you are forwarded to the logged in profile.",
        ],
      },

      settings: { 
        title: "Settings",
        bullets: [
           "In settings, the account can be managed....",
           "The settings section comprises 5 subsections: 1. Account settings, 2. Data & Privacy, 3. Security, 4. Profile Management, 5. Help & Support",
        ],

      },
      accountSettings: {
        title: "Account Settings",
        bullets: [
          "Profile name: The profile name cannot be changed. It is possible to share the profile name via Email to other users so they can find you.",
          "Change email: Update the Email linked to your account (used for login and account recovery). It is possible to change the Email by just confirming the new Email address by entering the code we sent to your new Email.",
          "Change password: Choose a new password to keep your account secure. It can be made in 2 steps. First step: confirm your old password. Second step: Enter your new password, and confirm the new password. After clicking Update Password your password has been changed.",
          "Language: Select the language used in the drop-down menu. The language changes immediately after selecting and clicking on the new language.",
          "Pause profile: Disable your profile and stop account activity until you reactivate. Confirm the deactivation by entering your current password. Reactivation is not requiring any password or other requirements. Your account and all your data will not be deleted by the deactivation. ",
        ],
      },

      dataPrivacy: {
        title: "Data & Privacy",
        bullets: [
          "Essential processing (required): Account, security, service integrity, and non-personalized operations. This is required to run the service and cannot be disabled.",
          "Location permission preferences: Control whether Elnydo can access your device location for location-based features.",
          "Push notifications: Enable/disable notifications and choose which notification types you want to receive.",
          "Anonymous analytics: Allow anonymous usage analytics to help improve performance and stability.",
          "Delete account and data: Permanently delete your account and associated data (this action may be irreversible). The process to delete the account and all data is made in 2 steps: First step: Enter your current password. Second step: Enter the 6-digit code we sent to your registered Email address. After confirming the account and all of your data is deleted without the possibility of recovery. This means all of your posts, conversations and settings are lost.",
          "Export Data: You have a right to your data and know about all of your data on this platform. The Export My Data function downloads a zip document including your data on our platform.",
          "Terms of Service & Privacy Policy: Opens the Terms of Service & Privacy Policy of Elnydo in a new tab.",
          "Data Protection: Opens the Data Protection documentation of Elnydo in a new tab."
        ],
      },

      security: {
        title: "Security",
        bullets: [
          "Two-factor authentication (2FA): Add an extra verification step when logging in. This is only possible through a second Email address. You get a code to the second Email which you need to confirm. There is also a range of codes which would help you in case the 2FA Email is not working anymore. You can remove the 2FA anytime.",
          "Log out from all devices: End active sessions on all devices to protect your account if you suspect unauthorized access.",
        ],
      },

      profileManagement: {     
        title: "Profile Management",
        bullets: [
          "Profile Management contains the links in form of buttons which are relevant for the appearance of your profile.",
          "The buttons comprise: My Profile, My Postings, Notifications, My Feed, Followeers and Blocked Users. They are described in the Platform Guide. ",
        ],
      },

      helpSupport: {
        title: "Help & Support",
        bullets: [
          "Help & Support provides guidance through the entire platform.",
          "It is separated into the following 4 subsections: Account administration, Platform Guide, Advertisement Guide and a Frequent asked questions (FAQ) section.",
          "Help & Support also comprises the possibility to enter in contact with Elnydo, when problems are caused....",
        ],
      },
    },
  },

  
  platform: {
    pageTitle: "Platform Guide",
    pageSubtitle:
      "This guide explains what you can do on Elnydo: explore posts, upload content, interact with others, manage your profile and posts, and use safety tools.",

    ui: {
      back: "Back",
      backAria: "Back",
      backTitle: "Back",
      jumpHint: "Jump to a topic:",
      overview: "Overview",
      copyLink: "Copy link",
      backToTop: "Back to top",
      linkToTopicAria: "Link to {{title}}",
      linkToTopicTitle: "Link to {{title}}",
    },

    topics: {
      gallery: {
        title: "Gallery",
        bullets: [
          "Gallery is the main feed for posts of Elnydo.",
          "Posts are filtered primarily by city, then by country, then by other posts (nearby countries are preferred).",
          "Inside each filter scope, posts are sorted by date (newest posts first).",
          "You can like a post and follow a user.",
          "It is possible to search in Gallery for Posts containing hidden hashtags. The hashtags are not shown in the post itself.",
        ],
      },

      preferences: {
        title: "My Feed",
        bullets: [
          "My Feed shows posts from users you follow, sorted by date. You can unfollow users and like posts there as well.",
          "It is possible to search in My Feed for posts containing hidden hashtags. The hashtags are not shown in the post itself.",
        ],
      },
      mylikes: {
        title: "My Likes",
        bullets: [
          "My Likes shows posts you have liked, sorted by the date you liked them. You can remove likes (unlike) at any time.",
          "It is possible to search in My Likes for posts containing hidden hashtags. The hashtags are not shown in the post itself.",
        ],
      },
      changeLocation: {
        title: "Change Location",
        bullets: [
          "You can change the location to see posts from a different place.",
          "Changing location affects the posts shown in the Gallery feed. Posts are filtered for the respective location.",
          "To change location, open Change Location from the sidebar.",
          "Choose a new location by clicking on it or searching for it.",
          "Confirm the selected location using the confirmation button in the left sidebar.",
          "You can quickly switch back to your current device location (fast change). This requires location permission to be enabled.",
        ],
      },

      uploadPost: {
        title: "Upload Post",
        bullets: [
          "Upload Post opens the posting page.",
          "On web, you can upload videos and pictures.",
          "On mobile, you can upload existing media or create a photo/video using your device camera.",
          "You can choose or create multiple media items. The limit is 8 media per post.",
          "A filter is provided for images and videos with different themes.",
          "You can add hidden hashtags to your post. These are used for search in Gallery, My Feed, and My Likes, but are not visible publicly.",
          "When the post is finished, click Upload to publish it publicly to the Gallery.",
        ],
      },

      musicInMedia: {
        title: "Music in Media",
        bullets: [
          "You can mix videos with a music track.",
          "In the upload flow, tap Music to open the list of available tracks.",
          "You can preview tracks, choose a title, and then select the part (interval) of the track you want to use.",
          "Music does not need to start at the beginning — you can choose a specific interval and mix it into the video.",
          "On mobile, you can also record a video with music in the background: open the camera and select music using the music icon. Choose a track and interval, then record with music playing in sync.",
        ],
      },

      myProfile: {
        title: "My Profile",
        bullets: [
          "My Profile shows your profile picture (or the default avatar), your profile name, your location, and your bio (“What you need to know about me”).",
          "Profile picture: if none is set, a default avatar is shown (ghost on a blue background).",
          "Profile name is displayed publicly and used in notifications.",
          "Location can be edited and can contain any text, but we recommend using a real city/country.",
          "Bio can be edited at any time.",
        ],
      },

      myPosts: {
        title: "My Posts",
        bullets: [
          "My Posts shows all posts you have uploaded.",
          "Each post shows its like count, and you can view who liked it (profile name and profile access by clicking).",
          "You can delete a post. Deleted posts are removed from public visibility.",
          "You can edit a post by removing media items from it (where supported).",
          "You can hide posts from public view without deleting them. Hidden posts can be restored (unhidden) at any time.",
        ],
      },

      followersFollowing: {
        title: "My Followers & I Follow",
        bullets: [
          "My Followers shows the list of users who follow you.",
          "I Follow shows the list of users you follow.",
          "In both lists you can view profile pictures and open profiles by clicking on the profile name or profile picture.",
          "You can block or block & report a user from these lists.",
        ],
      },

      visitProfiles: {
        title: "Visit Profiles",
        bullets: [
          "To visit a profile, click a profile name or profile picture anywhere in the app.",
          "A profile view opens showing profile picture, name, location, and bio.",
          "You can view the user’s posts and like them.",
          "You can block the profile if necessary.",
        ],
      },

      notifications: {
        title: "Notifications",
        bullets: [
          "Notifications inform you when someone likes your post, follows you, or writes you in chat.",
          "Notifications show the profile name, profile picture, and the date and time of the action.",
        ],
      },

      chat: {
        title: "Chat",
        bullets: [
          "Chat allows private messaging between users.",
          "You can send and receive text messages.",
          "Messages are protected using industry-standard security measures.",
          "If needed, you can block or report users directly from chat.",
        ],
      },

      safety: {
        title: "Safety: Blocking & Reporting",
        bullets: [
          "If you feel uncomfortable or see rule-breaking content, you can block or report users and posts.",
          "Blocking stops interaction and helps you avoid unwanted contact.",
          "Reporting sends content to moderation for review. Content that violates rules may be removed and accounts may be restricted.",
          "If there is an immediate danger, contact local authorities first — in-app reporting is not an emergency service.",
        ],
      },
    },
  },
} as const;

export default help;
